import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle, Zap, Users } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskedLine, Reveal } from "@/components/Reveal";

const PROCESS_STAGES = [
  {
    number: "01",
    title: "Discovery",
    duration: "3–10 days (scoped to project size)",
    icon: CheckCircle,
    whatHappens: [
      "Goals, constraints, systems map (APIs, n8n, data, auth)",
      "Success metrics agreed in writing",
      "Shape options: fixed pilot vs phased build"
    ],
    youGet: "Short brief / statement of work, timeline, engagement recommendation."
  },
  {
    number: "02", 
    title: "Build",
    duration: "Weeks, not mystery months — milestones on a shared board",
    icon: Zap,
    whatHappens: [
      "Iterative delivery (demos every sprint / week)",
      "AI features, n8n workflows, and/or React apps in production-minded repos",
      "Async updates in your Slack/email; one primary CodeRealm owner"
    ],
    youGet: "Working increments in staging → production, docs as we go."
  },
  {
    number: "03",
    title: "Handoff", 
    duration: "Optional retainer for care & iteration",
    icon: Users,
    whatHappens: [
      "Runbooks, env notes, workflow diagrams, admin access transfer",
      "Optional retainer for care & iteration"
    ],
    youGet: "You (or your team) can operate it — or we stay on under retainer."
  }
];

const ENGAGEMENT_MODELS = [
  {
    title: "Fixed-scope pilot / project",
    bestFor: "Clear problem, defined deliverables (e.g. one AI feature, one n8n ops lane, MVP slice).",
    howItWorks: "Discovery → fixed SOW → milestone payments.",
    pricing: "Typical pilots land in the $5k–$15k range; fuller builds $15k–$40k+ depending on integrations and risk. Final quote after discovery.",
    cta: "Start a project"
  },
  {
    title: "Monthly retainer",
    bestFor: "Ongoing automation, AI iteration, product velocity without hiring.",
    howItWorks: "Reserved capacity (e.g. N days/month), rolling backlog, monthly cadence.",
    pricing: "Retainers commonly start around $4k–$8k / month for focused capacity; scale with allocation.",
    cta: "Ask about retainer"
  }
];

const ProcessStage = ({ stage, delay = 0 }) => {
  const Icon = stage.icon;
  
  return (
    <Reveal delay={delay}>
      <div className="group rounded-2xl border border-border bg-card p-8 transition-all duration-300 hover:border-coral/20">
        <div className="flex items-start gap-6">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-coral/10 text-coral transition-colors duration-300 group-hover:bg-coral group-hover:text-white">
            <Icon className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-4">
              <span className="font-display text-3xl font-extrabold text-outline transition-colors duration-500 group-hover:text-coral group-hover:[-webkit-text-stroke:0px]">
                {stage.number}
              </span>
              <div>
                <h3 className="font-display text-2xl font-bold tracking-tight">
                  {stage.title}
                </h3>
                <p className="mt-1 text-sm text-coral font-mono">
                  {stage.duration}
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <h4 className="mb-2 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  What happens
                </h4>
                <ul className="space-y-1">
                  {stage.whatHappens.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="mt-2 block h-1 w-1 shrink-0 rounded-full bg-coral"></span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="mb-2 font-mono text-xs uppercase tracking-[0.2em] text-foreground">
                  You get
                </h4>
                <p className="text-sm text-foreground font-medium">
                  {stage.youGet}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  );
};

const EngagementModel = ({ model, delay = 0 }) => {
  return (
    <Reveal delay={delay}>
      <div className="rounded-2xl border border-border bg-card p-8">
        <h3 className="font-display text-xl font-bold tracking-tight mb-4">
          {model.title}
        </h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="mb-2 font-mono text-xs uppercase tracking-[0.2em] text-coral">
              Best for
            </h4>
            <p className="text-sm text-muted-foreground">
              {model.bestFor}
            </p>
          </div>
          
          <div>
            <h4 className="mb-2 font-mono text-xs uppercase tracking-[0.2em] text-coral">
              How it works
            </h4>
            <p className="text-sm text-muted-foreground">
              {model.howItWorks}
            </p>
          </div>
          
          <div>
            <h4 className="mb-2 font-mono text-xs uppercase tracking-[0.2em] text-coral">
              Typical range
            </h4>
            <p className="text-sm text-foreground font-medium">
              {model.pricing}
            </p>
          </div>
        </div>
        
        <div className="mt-6">
          <Link
            to="/contact"
            className="group inline-flex items-center gap-2 rounded-full bg-coral px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-navy dark:hover:bg-coral-bright"
          >
            {model.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </Reveal>
  );
};

export default function HowWeWork() {
  return (
    <>
      <Seo
        title="How We Work — codeRealm"
        siteName="codeRealm"
        description="Clear stages, clear ownership, clear engagement models — from first call to handoff. Discovery, build, handoff process with fixed-scope projects and monthly retainers."
      />
      
      <section className="mx-auto max-w-7xl px-5 pb-24 pt-20 sm:px-8 sm:pt-28">
        
        {/* Header */}
        <div className="mb-16">
          <MaskedLine>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral">Engagement</span>
          </MaskedLine>
          
          <h1 className="mt-8 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.12}>How we</MaskedLine>
            <MaskedLine delay={0.24}>
              <span className="text-outline">work</span>
            </MaskedLine>
          </h1>
          
          <Reveal delay={0.35} className="mt-6 max-w-2xl">
            <p className="text-xl leading-relaxed text-muted-foreground">
              Clear stages, clear ownership, clear engagement models — from first call to handoff.
            </p>
          </Reveal>
        </div>

        {/* Process stages */}
        <div className="mb-20">
          <Reveal delay={0.4}>
            <h2 className="mb-8 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Process — Discovery → Build → Handoff
            </h2>
          </Reveal>
          
          <div className="space-y-8">
            {PROCESS_STAGES.map((stage, i) => (
              <ProcessStage 
                key={stage.number} 
                stage={stage} 
                delay={0.5 + (i * 0.1)}
              />
            ))}
          </div>
        </div>

        {/* Engagement models */}
        <div className="mb-16">
          <Reveal delay={0.9}>
            <h2 className="mb-8 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Engagement models
            </h2>
          </Reveal>
          
          <div className="grid gap-8 lg:grid-cols-2">
            {ENGAGEMENT_MODELS.map((model, i) => (
              <EngagementModel 
                key={model.title} 
                model={model} 
                delay={1.0 + (i * 0.1)}
              />
            ))}
          </div>
        </div>

        {/* Comparison table */}
        <Reveal delay={1.3} className="mb-16">
          <div className="overflow-x-auto rounded-2xl border border-border bg-card">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="p-4 text-left font-mono text-xs uppercase tracking-[0.2em] text-coral">
                    
                  </th>
                  <th className="p-4 text-left font-display text-lg font-bold">
                    Fixed project
                  </th>
                  <th className="p-4 text-left font-display text-lg font-bold">
                    Retainer
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-4 font-mono text-sm font-medium text-coral">Scope</td>
                  <td className="p-4 text-sm text-muted-foreground">Defined up front</td>
                  <td className="p-4 text-sm text-muted-foreground">Flexible backlog</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-4 font-mono text-sm font-medium text-coral">Best when</td>
                  <td className="p-4 text-sm text-muted-foreground">Milestone / deadline</td>
                  <td className="p-4 text-sm text-muted-foreground">Continuous improvement</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-4 font-mono text-sm font-medium text-coral">Billing</td>
                  <td className="p-4 text-sm text-muted-foreground">Milestones</td>
                  <td className="p-4 text-sm text-muted-foreground">Monthly</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-4 font-mono text-sm font-medium text-coral">Change requests</td>
                  <td className="p-4 text-sm text-muted-foreground">Via change order</td>
                  <td className="p-4 text-sm text-muted-foreground">Absorbed in capacity</td>
                </tr>
                <tr>
                  <td className="p-4 font-mono text-sm font-medium text-coral">Handoff</td>
                  <td className="p-4 text-sm text-muted-foreground">End of project</td>
                  <td className="p-4 text-sm text-muted-foreground">Continuous + exit anytime</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Reveal>

        {/* What we need from you */}
        <Reveal delay={1.4} className="mb-16">
          <div className="rounded-2xl border border-border bg-card/60 p-8">
            <h3 className="font-display text-2xl font-bold tracking-tight mb-6">
              What we need from you
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="mt-2 block h-1 w-1 shrink-0 rounded-full bg-coral"></span>
                <span className="text-base text-muted-foreground">A decision-maker in the loop</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 block h-1 w-1 shrink-0 rounded-full bg-coral"></span>
                <span className="text-base text-muted-foreground">Access to the systems we'll touch (or a sandbox)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 block h-1 w-1 shrink-0 rounded-full bg-coral"></span>
                <span className="text-base text-muted-foreground">One success metric that matters in 30–90 days</span>
              </li>
            </ul>
          </div>
        </Reveal>

        {/* Final CTA */}
        <Reveal delay={1.5} className="text-center">
          <div className="border-t border-border pt-16">
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Ready when you are
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              We reply within 1 business day · boutique studio, India + remote
            </p>
            <div className="mt-8">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 rounded-full bg-coral px-8 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-navy dark:hover:bg-coral-bright"
              >
                Start a project
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </Reveal>

      </section>
    </>
  );
}